// ============================================
// Verification Page
// ============================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaArrowLeft, FaUpload, FaInfoCircle, FaTimes, FaCheckCircle,
  FaHome, FaChartBar, FaEye, FaFileAlt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './Verification.css';
import './Transaction.css';

const Verification = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [verificationData, setVerificationData] = useState({
    fullName: '',
    idCardNumber: '',
    idCardFront: null,
    idCardBack: null,
    selfieWithIdCard: null,
    otherDocuments: [],
    agreedToTerms: false
  });
  const [uploading, setUploading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('none');

  useEffect(() => {
    if (user) {
      if (user.isVerified) {
        setVerificationStatus('verified');
      } else if (user.verificationStatus === 'pending' || user.verificationStatus === 'rejected') {
        setVerificationStatus(user.verificationStatus);
      } else {
        setVerificationStatus('none');
      }
    }
  }, [user]);

  const handleFileChange = (field, file) => {
    if (file) {
      if (field === 'otherDocuments') {
        setVerificationData(prev => ({
          ...prev,
          otherDocuments: [...prev.otherDocuments, { file, name: file.name }]
        }));
      } else {
        setVerificationData(prev => ({
          ...prev,
          [field]: { file }
        }));
      }
    }
  };

  const removeDocument = (index) => {
    setVerificationData(prev => ({
      ...prev,
      otherDocuments: prev.otherDocuments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitVerification = async (e) => {
    e.preventDefault();
    
    if (!verificationData.fullName || !verificationData.idCardNumber) {
      toast.error('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    if (!verificationData.idCardFront || !verificationData.idCardBack) {
      toast.error('กรุณาอัพโหลดรูปบัตรประชาชนทั้งสองด้าน');
      return;
    }

    if (!verificationData.selfieWithIdCard) {
      toast.error('กรุณาอัพโหลดรูปถ่ายคู่บัตรประชาชน');
      return;
    }

    if (!verificationData.agreedToTerms) {
      toast.error('กรุณาอ่านและยอมรับเงื่อนไข');
      return;
    }

    setUploading(true);
    try {
      await api.submitVerification(verificationData);
      toast.success('ส่งข้อมูลยืนยันตัวตนสำเร็จ รอการตรวจสอบจากแอดมิน');
      setVerificationData({
        fullName: '',
        idCardNumber: '',
        idCardFront: null,
        idCardBack: null,
        selfieWithIdCard: null,
        otherDocuments: [],
        agreedToTerms: false
      });
      refreshUser();
      navigate('/profile');
    } catch (error) {
      toast.error(error.message || 'เกิดข้อผิดพลาดในการส่งข้อมูล');
    } finally {
      setUploading(false);
    }
  };

  if (verificationStatus === 'verified') {
    return (
      <div className="verification-page">
        <div className="verification-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="verification-verified"
          >
            <FaCheckCircle className="verified-icon" />
            <h2>ยืนยันตัวตนแล้ว</h2>
            <p>บัญชีของคุณได้รับการยืนยันตัวตนเรียบร้อยแล้ว</p>
            <Link to="/profile" className="btn-back">
              <FaArrowLeft />
              <span>กลับไปหน้าโปรไฟล์</span>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="verification-page">
      {/* Header */}
      <div className="transaction-header">
        <Link to="/profile" className="btn-back">
          <FaArrowLeft />
        </Link>
        <h1>ยืนยันตัวตน</h1>
        {verificationStatus === 'pending' && (
          <div className="status-badge pending">
            <span>รอการตรวจสอบ</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="verification-content">
        <form onSubmit={handleSubmitVerification} className="verification-form">
          <div className="form-group">
            <label>ชื่อ-นามสกุล</label>
            <input
              type="text"
              className="input"
              placeholder="กรอกชื่อ-นามสกุล"
              value={verificationData.fullName}
              onChange={(e) => setVerificationData(prev => ({ ...prev, fullName: e.target.value }))}
              required
              disabled={verificationStatus === 'pending'}
            />
          </div>

          <div className="form-group">
            <label>เลขบัตรประชาชน</label>
            <input
              type="text"
              className="input"
              placeholder="กรอกเลขบัตรประชาชน 13 หลัก"
              value={verificationData.idCardNumber}
              onChange={(e) => setVerificationData(prev => ({ ...prev, idCardNumber: e.target.value }))}
              maxLength={13}
              required
              disabled={verificationStatus === 'pending'}
            />
          </div>

          <div className="form-group">
            <label>รูปบัตรประชาชน (ด้านหน้า)</label>
            <div className="file-upload-wrapper">
              <input
                type="file"
                accept="image/*"
                className="file-input"
                id="idCardFrontInput"
                onChange={(e) => handleFileChange('idCardFront', e.target.files[0])}
                required
                disabled={verificationStatus === 'pending'}
              />
              <label htmlFor="idCardFrontInput" className="file-upload-btn">
                <FaUpload />
                <span>อัพโหลดรูป</span>
              </label>
              {verificationData.idCardFront && (
                <div className="uploaded-file-name">
                  <span>{verificationData.idCardFront.file.name}</span>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>รูปบัตรประชาชน (ด้านหลัง)</label>
            <div className="file-upload-wrapper">
              <input
                type="file"
                accept="image/*"
                className="file-input"
                id="idCardBackInput"
                onChange={(e) => handleFileChange('idCardBack', e.target.files[0])}
                required
                disabled={verificationStatus === 'pending'}
              />
              <label htmlFor="idCardBackInput" className="file-upload-btn">
                <FaUpload />
                <span>อัพโหลดรูป</span>
              </label>
              {verificationData.idCardBack && (
                <div className="uploaded-file-name">
                  <span>{verificationData.idCardBack.file.name}</span>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>ถ่ายรูปคู่บัตรประชาชน</label>
            <div className="file-upload-wrapper">
              <input
                type="file"
                accept="image/*"
                className="file-input"
                id="selfieWithIdCardInput"
                onChange={(e) => handleFileChange('selfieWithIdCard', e.target.files[0])}
                required
                disabled={verificationStatus === 'pending'}
              />
              <label htmlFor="selfieWithIdCardInput" className="file-upload-btn">
                <FaUpload />
                <span>อัพโหลดรูป</span>
              </label>
              {verificationData.selfieWithIdCard && (
                <div className="uploaded-file-name">
                  <span>{verificationData.selfieWithIdCard.file.name}</span>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>เอกสารอื่นๆ (ถ้ามี)</label>
            <div className="file-upload-wrapper">
              <input
                type="file"
                accept="image/*,.pdf"
                className="file-input"
                onChange={(e) => handleFileChange('otherDocuments', e.target.files[0])}
                multiple
                disabled={verificationStatus === 'pending'}
              />
              <div className="file-upload-btn">
                <FaUpload />
                <span>อัพโหลดเอกสาร</span>
              </div>
              {verificationData.otherDocuments.length > 0 && (
                <div className="uploaded-documents">
                  {verificationData.otherDocuments.map((doc, index) => (
                    <div key={index} className="document-item">
                      <span>{doc.name}</span>
                      <button
                        type="button"
                        onClick={() => removeDocument(index)}
                        className="remove-doc-btn"
                        disabled={verificationStatus === 'pending'}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="terms-checkbox">
            <label>
              <input
                type="checkbox"
                checked={verificationData.agreedToTerms}
                onChange={(e) => setVerificationData(prev => ({ ...prev, agreedToTerms: e.target.checked }))}
                required
                disabled={verificationStatus === 'pending'}
              />
              <span>ฉันได้อ่านและยอมรับเงื่อนไขการยืนยันตัวตน</span>
            </label>
          </div>

          <div className="terms-info">
            <FaInfoCircle />
            <div>
              <strong>เงื่อนไขการยืนยันตัวตน:</strong>
              <ul>
                <li>กรุณาอัพโหลดรูปบัตรประชาชนทั้งสองด้านที่ชัดเจน</li>
                <li>รูปภาพต้องเห็นรายละเอียดชัดเจน สามารถอ่านเลขบัตรประชาชนได้</li>
                <li>ข้อมูลที่ส่งจะถูกเก็บเป็นความลับและใช้สำหรับการยืนยันตัวตนเท่านั้น</li>
                <li>การยืนยันตัวตนจะใช้เวลาประมาณ 1-3 วันทำการ</li>
                <li>กรุณารอการตรวจสอบจากแอดมิน</li>
              </ul>
            </div>
          </div>

          {verificationStatus === 'pending' ? (
            <div className="pending-message">
              <p>คุณได้ส่งคำขอยืนยันตัวตนแล้ว กรุณารอการตรวจสอบจากแอดมิน</p>
            </div>
          ) : (
            <button
              type="submit"
              className="submit-verification-btn"
              disabled={uploading}
            >
              {uploading ? 'กำลังส่งข้อมูล...' : 'ส่งข้อมูลยืนยันตัวตน'}
            </button>
          )}
        </form>
      </div>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <Link to="/" className="nav-item">
          <FaHome />
          <span>Home</span>
        </Link>
        <Link to="/trading" className="nav-item">
          <FaChartBar />
          <span>Trade</span>
        </Link>
        <Link to="/history" className="nav-item">
          <FaEye />
          <span>History</span>
        </Link>
        <Link to="/profile" className="nav-item active">
          <FaFileAlt />
          <span>Profile</span>
        </Link>
      </nav>
    </div>
  );
};

export default Verification;

